import { useState } from "react";
import FluxiLogo from "@/assets/svg/FluxiLogoWhite.svg";
import EmailStep from "@/components/auth/steps/EmailStep";
import CreateAccountStep from "@/components/auth/steps/CreateAccountStep";
import CodeVerificationStep from "@/components/auth/steps/CodeVerificationStep";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import toast from "react-hot-toast";
import { postSendCodeEmail } from "@/services/auth/authAxios/authAxios";
import { useMetaPixel } from "@/hooks/useMetaPixel";
import { useEffect } from "react";

import {
  buildPendingUserDetailFromForm,
  emailExistsService,
  setPendingUserDetail,
  userEmailService,
} from "@/feature/auth";
import { useSearchParams } from "react-router-dom";

interface UserData {
  email: string;
}

export const SwitchGuestPage = () => {
  const [searchParams] = useSearchParams();
  const { trackEvent } = useMetaPixel();
  const { language, setLanguage } = useAuth();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [userData, setUserData] = useState<UserData>({
    email: "",
  });
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const updateUserData = (data: Partial<UserData>) => {
    setUserData((prev) => ({ ...prev, ...data }));
  };

  const goToStep = (step: 1 | 2 | 3) => setCurrentStep(step);

  const handleClose = () => {
    setCurrentStep(1);
    setError("");
    setIsLoading(false);
    updateUserData({ email: "" });
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.innerHTML = `
      var head = document.head;
      var script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = "https://213502.t.hyros.com/v1/lst/universal-script?ph=8037809a1b4fa13f47ce2be500afd8cdb7b5cdce93d6ab85b86324c2b73106b0&tag=!clicked&ref_url=" + encodeURI(document.URL) ;
      head.appendChild(script);
    `;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    trackEvent("Purchase", {
      ...(searchParams.get("order_id")
        ? { order_id: searchParams.get("order_id") }
        : {})
    });
  }, []);

  const handleChangeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("user_language", lang);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <EmailStep
            onClose={handleClose}
            email={userData.email}
            onNext={async (email) => {
              setError("");
              setIsLoading(true);
              try {
                updateUserData({ email });
                const res = await emailExistsService.getByEmail(email);
                if (res.exists) {
                  toast.error("Este correo ya está registrado. Usa otro correo.");
                  return;
                }
                goToStep(2);
              } catch (e: any) {
                console.error(e);
                toast.error(e?.message ?? "No pudimos validar el correo. Inténtalo de nuevo.");
              } finally {
                setIsLoading(false);
              }
            }}
          />
        );
      case 2:
        return (
          <CreateAccountStep
            email={userData.email}
            isFromSwitchGuest={true}
            isLoading={isLoading}
            error={error}
            onBack={() => goToStep(1)}
            onSubmit={async ({ fullName, phoneNumber }) => {
              if (!fullName.trim()) {
                setError(
                  dictionary["name-required"]?.[language as Language] ??
                  "El nombre es requerido"
                );
                return;
              }
              if (!phoneNumber.trim()) {
                setError(
                  dictionary["phone-required"]?.[language as Language] ??
                  "El celular es requerido"
                );
                return;
              }

              setError("");
              setIsLoading(true);
              try {
                const detailPayload = buildPendingUserDetailFromForm({
                  fullName,
                  phoneNumber,
                });

                setPendingUserDetail(detailPayload);
                const resUserEmail = await userEmailService.update(userData.email);
                localStorage.setItem("access_token", resUserEmail.access_token);
                localStorage.setItem("refresh_token", resUserEmail.refresh_token);

                const resSendCode = await postSendCodeEmail({ email: userData.email });
                toast.success(resSendCode.data.message);
                goToStep(3);
              } catch (e: any) {
                console.error(e);
                toast.error(e?.message ?? "No pudimos enviar el código. Inténtalo de nuevo.");
              } finally {
                setIsLoading(false);
              }
            }}
          />
        );
      case 3:
        return (
          <CodeVerificationStep
            onClose={handleClose}
            email={userData.email}
            onNext={() => { }}
            onBack={() => goToStep(1)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-no-repeat"
      style={{
        backgroundImage: "url('/assets/images/login-background.webp')",
      }}
    >
      {/* Language switcher top right */}
      <div className="absolute top-6 right-6 z-50">
        <LanguageSwitcher
          currentLanguage={language}
          languages={Object.values(Language)}
          onChangeLanguage={handleChangeLanguage}
        />
      </div>

      <div
        className={`flex flex-row max-md:flex-col max-md:gap-8 w-full max-w-[1200px] h-auto ${currentStep === 2 || currentStep === 3 ? "md:h-[90vh]" : "md:h-[500px]"
          } rounded-2xl ${currentStep === 2 || currentStep === 3 ? "overflow-visible" : "overflow-hidden"
          } bg-opacity-90 px-4 md:px-0`}
      >
        {/* Left Section */}
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center px-4 md:px-8 text-white py-8 md:py-0">
          <div className="text-center text-white">
            <h1 className="text-[32px] md:text-[32px] lg:text-[36px] font-bold mb-1 leading-tight pt-0 md:pt-0">
              <span className="font-semibold">
                {dictionary["sales-by-"]?.[language as Language] ??
                  "Ventas como por"}
              </span>
            </h1>
            <h2
              className="text-[32px] md:text-[28px] lg:text-[32px] italic font-light leading-tight "
              style={{
                fontFamily: "'Playfair Display', serif",
                lineHeight: "1",
              }}
            >
              <span className="italic text-[#4DB5FF]">
                {dictionary["magic-art"]?.[language as Language] ?? "arte de magia"}
              </span>
            </h2>
          </div>
        </div>

        {/* Right Section */}
        <div
          className={`w-full md:w-[474px] max-w-[90%] p-6 md:px-[64px] md:py-[20px] flex flex-col items-center rounded-[20px] border-2 border-[#4DB5FF] backdrop-blur-md mx-auto md:mx-0 min-h-[300px] overflow-visible ${currentStep === 2 || currentStep === 3
            ? "md:min-h-0 md:h-full md:max-h-[calc(90vh-40px)] md:overflow-y-auto md:overflow-x-hidden"
            : "md:h-auto justify-center md:overflow-hidden"
            }`}
          style={{
            background: "rgba(255, 255, 255, 0.08)",
            boxSizing: "border-box",
          }}
        >
          {/* Wrapper: con my-auto se centra si cabe, si no cabe queda arriba y hace scroll */}
          <div
            className={`flex flex-col items-center gap-4 w-full ${currentStep === 2 || currentStep === 3 ? "my-auto flex-shrink-0" : ""
              }`}
          >
            <img
              src={FluxiLogo}
              alt="Fluxi Logo"
              className="h-16 md:h-auto mb-2"
            />

            {/* Title Section */}
            {currentStep === 1 && (
              <div className="flex flex-col items-center gap-1">
                <h2 className="text-[24px] md:text-[30px] font-bold text-white text-center">
                  {dictionary["enter-your-email"]?.[language as Language] ??
                    "Ingresa tu correo"}
                </h2>
              </div>
            )}

            {renderStep()}
          </div>
        </div>
      </div>
    </div>
  );
};